import { useMemo, useState, useEffect, type CSSProperties } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useActiveAthlete } from "@/hooks/useActiveAthlete";
import RecruitingTimeline from "@/components/RecruitingTimeline";
import AcademicRequirementsTracker from "@/components/AcademicRequirementsTracker";
import CoachEmailWriter from "@/components/CoachEmailWriter";
import { apiRequest } from "@/lib/api";
import {
  type CalendarPeriod,
  getCalendarCurrentPeriod,
  getCalendarPeriodStatus,
  getCalendarKeyDates,
  matchSportKey,
} from "@/lib/recruitingData";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  School,
  Calendar,
  Briefcase,
  Check,
  Award,
  Star,
  Clock,
  FileText,
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  ExternalLink,
  CheckCircle2,
  Circle,
  X,
  Wand2,
  Pencil,
  Trash2,
  Plus,
  AlertCircle,
  PenLine,
  ChevronsUpDown,
  LucideIcon,
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NCAA_SCHOOLS, getSchoolLogoUrl, type NCAASchool } from "@/lib/ncaaSchools";
import { useToast } from "@/hooks/use-toast";

const legacyTheme: CSSProperties = {
  "--background": "220 58% 9%",
  "--foreground": "0 0% 100%",
  "--card": "221 45% 14%",
  "--card-foreground": "0 0% 100%",
  "--muted": "221 35% 18%",
  "--muted-foreground": "215 20% 80%",
  "--border": "40 70% 55%",
  "--input": "221 25% 20%",
  "--ring": "40 70% 55%",
};

// ---- Types ----
type RecruitingPeriodType = "contact" | "quiet" | "dead" | "evaluation" | "shutdown";

type RecruitingPeriodStatus = {
  id: string;
  periodType: RecruitingPeriodType;
  label: string;
  status: "current" | "upcoming" | "inactive";
  primaryText: string;
  rangeText: string;
  secondaryText: string;
};

type RecruitingEvent = {
  id: string;
  title: string;
  description?: string;
  date: string;
  sportType?: string;
  division?: string;
  isNcaaEvent?: boolean;
};


type Athlete = {
  id: string;
  name?: string;
  sports?: string;
  graduationYear?: number;
  position?: string;
  gpa?: number;
};

type CollegeInterest = {
  id: string;
  collegeName: string;
  espnId?: number;
  interestLevel: number;
  notes: string;
  addedDate: string;
};

type AthleteMilestone = {
  date: string;
  title: string;
  category: "test" | "application" | "financial" | "eligibility" | "signing";
  description: string;
};

// ---- Static Data ----

const DIV_LABEL: Record<"D1" | "D2", string> = { D1: "DI", D2: "DII" };

const PERIOD_INFO: Record<RecruitingPeriodType, {
  color: string;
  bgColor: string;
  borderColor: string;
  label: string;
  canDo: string[];
  cannotDo: string[];
}> = {
  contact: {
    color: "text-emerald-400",
    bgColor: "bg-emerald-900/30",
    borderColor: "border-emerald-500/40",
    label: "Contact Period",
    canDo: [
      "Coaches can make in-person contact anywhere (school, home, events)",
      "Coaches can attend your games and practices",
      "Official and unofficial campus visits are permitted",
      "Phone calls, emails, and texts are permitted",
    ],
    cannotDo: [],
  },
  quiet: {
    color: "text-amber-400",
    bgColor: "bg-amber-900/20",
    borderColor: "border-amber-500/40",
    label: "Quiet Period",
    canDo: [
      "Phone calls and emails from coaches",
      "In-person contact on the college's campus only",
      "Unofficial campus visits (you travel to the school)",
    ],
    cannotDo: [
      "Coaches visiting your home, school, or events",
      "In-person contact anywhere off campus",
    ],
  },
  dead: {
    color: "text-rose-400",
    bgColor: "bg-rose-900/20",
    borderColor: "border-rose-500/40",
    label: "Dead Period",
    canDo: [
      "Phone calls and emails from coaches are still OK",
    ],
    cannotDo: [
      "In-person contact of any kind",
      "Coaches attending your games or events",
      "Campus visits (official or unofficial)",
    ],
  },
  evaluation: {
    color: "text-sky-400",
    bgColor: "bg-sky-900/20",
    borderColor: "border-sky-500/40",
    label: "Evaluation Period",
    canDo: [
      "Coaches can watch you compete or practice",
      "Phone calls and emails from coaches",
    ],
    cannotDo: [
      "In-person contact off campus",
      "Home visits from coaches",
    ],
  },
  shutdown: {
    color: "text-violet-400",
    bgColor: "bg-violet-900/20",
    borderColor: "border-violet-500/40",
    label: "Recruiting Shutdown",
    canDo: [],
    cannotDo: [
      "Any recruiting activity whatsoever",
      "Phone calls or emails from coaches",
      "Campus visits",
      "Coach evaluations",
    ],
  },
};

const CHECKLIST_ITEMS: Array<{ id: string; title: string; description: string; deadline?: string; icon: LucideIcon }> = [
  { id: "resume", title: "Create Athletic Resume", description: "Summarize your athletic accomplishments, stats, and academic info in one page.", icon: FileText },
  { id: "ncaa", title: "Register with NCAA Eligibility Center", description: "Required for DI and DII athletes. Complete registration and send official transcripts.", deadline: "Before Junior Year", icon: Check },
  { id: "video", title: "Create Recruiting Video", description: "Compile highlights showcasing your skills, athleticism, and game awareness.", icon: Award },
  { id: "schools", title: "Research Target Schools", description: "Identify 10-15 schools that match your athletic and academic profile.", icon: School },
  { id: "sat", title: "Take SAT/ACT", description: "Register for and complete standardized tests needed for NCAA eligibility.", deadline: "Junior Year", icon: Calendar },
  { id: "coaches", title: "Contact College Coaches", description: "Send introduction emails with your athletic resume, video link, and academic info.", icon: Mail },
  { id: "showcases", title: "Attend Showcase Events", description: "Participate in events and camps where college coaches will be evaluating prospects.", icon: Briefcase },
];

const D1_CORE_COURSES = [
  { subject: "English", required: "4 years", detail: "Grammar, composition, literature" },
  { subject: "Math", required: "3 years", detail: "Algebra I or higher" },
  { subject: "Natural/Physical Science", required: "2 years", detail: "Including 1 year of lab science" },
  { subject: "Social Science", required: "2 years", detail: "History, psychology, geography, etc." },
  { subject: "Additional English, Math, or Science", required: "1 year", detail: "Counts toward the 16-course total" },
  { subject: "Elective Core Courses", required: "4 years", detail: "Foreign language, philosophy, comparative religion, or additional core subjects" },
];

const D2_CORE_COURSES = [
  { subject: "English", required: "3 years", detail: "Grammar, composition, literature" },
  { subject: "Math", required: "2 years", detail: "Algebra I or higher" },
  { subject: "Natural/Physical Science", required: "2 years", detail: "Including 1 year of lab science" },
  { subject: "Social Science", required: "2 years", detail: "History, psychology, geography, etc." },
  { subject: "Additional English, Math, or Science", required: "3 years", detail: "Count toward the 16-course total" },
  { subject: "Elective Core Courses", required: "4 years", detail: "Foreign language, philosophy, comparative religion, or additional core subjects" },
];

// Sorted by sport name, then gender (Men's before Women's). Only calendar-backed sports appear
// here (this dropdown drives the NCAA recruiting calendar); Tennis / Other are athlete-profile
// options with no calendar.
const SPORT_OPTIONS = [
  { value: "baseball", label: "Baseball" },
  { value: "mens-basketball", label: "Men's Basketball" },
  { value: "womens-basketball", label: "Women's Basketball" },
  { value: "cross-country", label: "Cross Country" },
  { value: "football", label: "Football" },
  { value: "mens-golf", label: "Men's Golf" },
  { value: "womens-golf", label: "Women's Golf" },
  { value: "mens-soccer", label: "Men's Soccer" },
  { value: "womens-soccer", label: "Women's Soccer" },
  { value: "softball", label: "Softball" },
  { value: "track-and-field", label: "Track and Field" },
  { value: "mens-volleyball", label: "Men's Volleyball" },
  { value: "womens-volleyball", label: "Women's Volleyball" },
];

// When coaches can first contact a prospect — DI rules (2025-26)
const FIRST_CONTACT_RULES: Record<string, { email: string; phone: string; inPerson: string }> = {
  softball: {
    email: "September 1 of junior year (email and text)",
    phone: "September 1 of junior year",
    inPerson: "September 1 of junior year (campus only during Quiet Period; unrestricted during Contact Period)",
  },
  baseball: {
    email: "August 1 before junior year of high school (email and text)",
    phone: "September 1 of junior year of high school",
    inPerson: "September 1 of junior year (campus only initially)",
  },
  "mens-basketball": {
    email: "June 15 after sophomore year (email and text)",
    phone: "June 15 after sophomore year",
    inPerson: "Start of junior year classes (on-campus, home, and school visits only)",
  },
  "womens-basketball": {
    email: "June 1 after sophomore year (email and text)",
    phone: "June 1 after sophomore year",
    inPerson: "September 1 of junior year (on-campus visits; off-campus contact not until senior year)",
  },
  "womens-volleyball": {
    email: "June 15 after sophomore year (email and text)",
    phone: "June 15 after sophomore year",
    inPerson: "August 1 before junior year (campus only during Quiet Period)",
  },
  "mens-volleyball": {
    email: "June 15 after sophomore year (email and text)",
    phone: "June 15 after sophomore year",
    inPerson: "August 1 before junior year (off-campus contact permitted during contact periods)",
  },
  "mens-soccer": {
    email: "September 1 of junior year (email and text)",
    phone: "September 1 of junior year",
    inPerson: "September 1 of junior year (campus-only visits during quiet periods; unrestricted during contact periods)",
  },
  "womens-soccer": {
    email: "September 1 of junior year (email and text)",
    phone: "September 1 of junior year",
    inPerson: "September 1 of junior year (campus-only visits during quiet periods; unrestricted during contact periods)",
  },
  "cross-country": {
    email: "September 1 of junior year (email and text)",
    phone: "August 22 (start of contact period in 2025-26 calendar year)",
    inPerson: "August 22 (start of contact period; no off-campus contact Aug. 1-21 evaluation period)",
  },
  "track-and-field": {
    email: "September 1 of junior year (email and text)",
    phone: "August 22 (start of contact period in 2025-26 calendar year)",
    inPerson: "August 22 (start of contact period; no off-campus contact Aug. 1-21 evaluation period)",
  },
  football: {
    email: "June 15 after sophomore year (email, text, and direct messages)",
    phone: "June 15 after sophomore year (unlimited calls except during dead and quiet periods)",
    inPerson: "September 1 of junior year for off-campus contact, and only during contact periods (limit of six off-campus contacts per prospect)",
  },
  "mens-golf": {
    email: "June 15 after sophomore year (email and text)",
    phone: "June 15 after sophomore year (coaches may call, text, and return messages)",
    inPerson: "Unofficial and official visits may begin August 1 before junior year; off-campus contact only during contact periods",
  },
  "womens-golf": {
    email: "June 15 after sophomore year (email and text)",
    phone: "June 15 after sophomore year (coaches may call, text, and return messages)",
    inPerson: "Unofficial and official visits may begin August 1 before junior year; off-campus contact only during contact periods",
  },
};

// NLI and signing period dates for 2025-26 cycle
const SIGNING_PERIODS_2025_26: Record<string, Array<{ label: string; dates: string; note?: string }>> = {
  softball: [
    { label: "NLI Signing Period", dates: "February 4, 2026 – August 1, 2026", note: "Spring sport — no separate early signing period" },
  ],
  baseball: [
    { label: "Early Signing Period", dates: "November 13–20, 2025" },
    { label: "Late Signing / NLI Period", dates: "April 16 – August 1, 2026" },
  ],
  "mens-basketball": [
    { label: "Early Signing Period", dates: "November 13–20, 2025" },
    { label: "Late Signing / NLI Period", dates: "April 16 – May 21, 2026" },
  ],
  "womens-basketball": [
    { label: "Early Signing Period", dates: "November 13–20, 2025" },
    { label: "Late Signing / NLI Period", dates: "April 16 – May 21, 2026" },
  ],
  "womens-volleyball": [
    { label: "Signing Period", dates: "December 15, 2025 – August 1, 2026", note: "Volleyball has one extended signing period" },
  ],
  "mens-volleyball": [
    { label: "Early Signing Period", dates: "November 13–20, 2025" },
    { label: "Late Signing / NLI Period", dates: "April 16 – August 1, 2026" },
  ],
  "mens-soccer": [
    { label: "Early Signing Period", dates: "November 13–20, 2025" },
    { label: "NLI Signing Period", dates: "February 4, 2026 – August 1, 2026" },
  ],
  "womens-soccer": [
    { label: "Early Signing Period", dates: "November 13–20, 2025" },
    { label: "NLI Signing Period", dates: "February 4, 2026 – August 1, 2026" },
  ],
  "cross-country": [
    { label: "Early Signing Period", dates: "November 13–20, 2025" },
    { label: "NLI Signing Period", dates: "February 4, 2026 – August 1, 2026" },
  ],
  "track-and-field": [
    { label: "Early Signing Period", dates: "November 13–20, 2025" },
    { label: "NLI Signing Period", dates: "February 4, 2026 – August 1, 2026" },
  ],
  football: [
    { label: "Early Signing Period", dates: "December 3–5, 2025", note: "Football signs earlier than most sports; the December window is when the majority of FBS/FCS prospects sign" },
    { label: "Regular Signing Period", dates: "February 4, 2026 – April 1, 2026" },
  ],
  "mens-golf": [
    { label: "Early Signing Period", dates: "November 13–20, 2025" },
    { label: "NLI Signing Period", dates: "February 4, 2026 – August 1, 2026" },
  ],
  "womens-golf": [
    { label: "Early Signing Period", dates: "November 13–20, 2025" },
    { label: "NLI Signing Period", dates: "February 4, 2026 – August 1, 2026" },
  ],
};

// Student-athlete action milestones — upcoming items shown in the sidebar
const STUDENT_ATHLETE_MILESTONES_2025_26: AthleteMilestone[] = [
  { date: "2026-05-03", title: "SAT Test Date", category: "test", description: "Register at collegeboard.org (deadline ~6 weeks before)." },
  { date: "2026-06-07", title: "SAT Test Date", category: "test", description: "Last SAT before summer. Register at collegeboard.org." },
  { date: "2026-06-13", title: "ACT Test Date", category: "test", description: "Summer ACT. Register at act.org (deadline ~5 weeks before)." },
  { date: "2026-07-11", title: "ACT Test Date", category: "test", description: "Summer ACT. Register at act.org." },
  { date: "2026-08-01", title: "Common App Opens", category: "application", description: "New Common Application cycle opens for fall 2027 enrollment." },
  { date: "2026-08-29", title: "SAT Test Date", category: "test", description: "Back-to-school SAT. Register at collegeboard.org." },
  { date: "2026-09-01", title: "NCAA Eligibility Center Check", category: "eligibility", description: "Confirm your Eligibility Center profile is current before senior year. Transcripts must be on file." },
  { date: "2026-09-12", title: "ACT Test Date", category: "test", description: "Fall ACT. Register at act.org." },
  { date: "2026-10-01", title: "FAFSA Opens", category: "financial", description: "Free Application for Federal Student Aid for 2027-28 school year. File early for the best aid." },
  { date: "2026-10-03", title: "SAT Test Date", category: "test", description: "Fall SAT. Register at collegeboard.org." },
  { date: "2026-10-24", title: "ACT Test Date", category: "test", description: "Fall ACT. Register at act.org." },
  { date: "2026-11-01", title: "Early Decision / Early Action", category: "application", description: "Typical early application deadline for many universities. Check each school individually." },
  { date: "2026-11-07", title: "SAT Test Date", category: "test", description: "Fall SAT. Register at collegeboard.org." },
  { date: "2026-11-12", title: "Early Signing Period Opens", category: "signing", description: "DI/DII early signing period typically opens mid-November. Confirm exact date with your compliance office." },
];

const OFFICIAL_RESOURCES = [
  { title: "DI/DII Recruiting Calendar", url: "https://www.ncaa.org/sports/2018/5/8/division-i-and-ii-recruiting-calendars.aspx" },
  { title: "Core Courses (NCSA Guide)", url: "https://www.ncsasports.org/ncaa-eligibility-center/ncaa-core-courses" },
  { title: "NCAA Eligibility Center", url: "https://web3.ncaa.org/hsportal/exec/homeNational" },
  { title: "Future Student-Athletes", url: "https://www.ncaa.org/sports/2021/2/8/student-athletes-future.aspx" },
];

const MILESTONE_COLORS: Record<AthleteMilestone["category"], string> = {
  test: "bg-sky-100 text-sky-700",
  application: "bg-purple-100 text-purple-700",
  financial: "bg-emerald-100 text-emerald-700",
  eligibility: "bg-amber-100 text-amber-700",
  signing: "bg-rose-100 text-rose-700",
};

// ---- Helpers ----

async function fetchJson<T>(path: string): Promise<T> {
  const res = await apiRequest(path);
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload?.message || `Request failed: ${res.status}`);
  }
  return res.json();
}

async function fetchAthleteProfile(): Promise<Athlete | null> {
  const res = await apiRequest("/athletes/me");
  if (res.status === 404) return null;
  if (!res.ok) throw new Error("Failed to fetch athlete profile");
  return res.json();
}

function formatShortDate(date: string): string {
  return new Date(`${date}T00:00:00.000Z`).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}

function getGradYearMessage(graduationYear?: number): { title: string; body: string } {
  if (!graduationYear) return {
    title: "Set graduation year for class-based guidance",
    body: "Add a graduation year to unlock grade-level recruiting reminders and deadline priorities.",
  };
  const yearsUntil = graduationYear - new Date().getFullYear();
  if (yearsUntil >= 3) return { title: `Class of ${graduationYear}: build the foundation now`, body: "Focus on core courses, game film quality, and building your college target list early." };
  if (yearsUntil === 2) return { title: `Class of ${graduationYear}: increase coach visibility`, body: "This is a strong window for consistent outreach, updated highlights, and showcase planning." };
  if (yearsUntil === 1) return { title: `Class of ${graduationYear}: recruiting is in a key phase`, body: "Prioritize active coach follow-up, visit planning, and eligibility milestones this year." };
  return { title: `Class of ${graduationYear}: senior-year closeout`, body: "Stay on top of final eligibility steps, communication cadence, and commitment decisions." };
}

function normalizeSportKey(sport: string): string {
  return matchSportKey(sport) ?? "softball";
}

function getUpcomingMilestones(today: Date, count = 6): Array<AthleteMilestone & { daysUntil: number }> {
  const todayStr = today.toISOString().slice(0, 10);
  return STUDENT_ATHLETE_MILESTONES_2025_26
    .filter((m) => m.date >= todayStr)
    .slice(0, count)
    .map((m) => ({
      ...m,
      daysUntil: Math.max(0, Math.ceil(
        (new Date(`${m.date}T00:00:00Z`).getTime() - today.getTime()) / 86400000
      )),
    }));
}

// ---- Sub-components ----

function ContactStatusWidget({
  currentPeriod,
  division,
  sport,
}: {
  currentPeriod: CalendarPeriod | null;
  division: string;
  sport: string;
}) {
  const periodType: RecruitingPeriodType | null = (currentPeriod?.type as RecruitingPeriodType) ?? null;

  if (!periodType) {
    return (
      <div className="rounded-xl border border-[#415A77]/40 bg-[#0B1220]/60 p-4 text-sm text-slate-400">
        No active period on record for this sport and division. Check the official NCAA calendar PDF for current dates.
      </div>
    );
  }

  const info = PERIOD_INFO[periodType];
  const endDateStr = currentPeriod?.endDate;
  const daysLeft = endDateStr
    ? Math.max(0, Math.ceil((new Date(`${endDateStr}T00:00:00Z`).getTime() - Date.now()) / 86400000))
    : null;
  const description = currentPeriod?.description;

  return (
    <div className={`rounded-xl border p-5 ${info.bgColor} ${info.borderColor}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">
            Right Now — {division} {sport}
          </p>
          <h3 className={`font-montserrat font-bold text-xl ${info.color}`}>{info.label}</h3>
          {description && <p className="text-sm text-slate-300 mt-1">{description}</p>}
        </div>
        {daysLeft !== null && daysLeft > 0 && (
          <div className="text-center">
            <p className={`text-3xl font-bold ${info.color}`}>{daysLeft}</p>
            <p className="text-xs text-slate-400">days left</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {info.canDo.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-400 mb-2">Coaches CAN</p>
            <ul className="space-y-1.5">
              {info.canDo.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-200">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        {info.cannotDo.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-rose-400 mb-2">Coaches CANNOT</p>
            <ul className="space-y-1.5">
              {info.cannotDo.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                  <X className="h-4 w-4 text-rose-400 flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}
        {info.cannotDo.length === 0 && info.canDo.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-emerald-300">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            No off-campus restrictions during Contact Period
          </div>
        )}
      </div>
    </div>
  );
}

function KeyMilestonesSection({ sport, division }: { sport: string; division: "D1" | "D2" }) {
  const contactRules = FIRST_CONTACT_RULES[sport] || FIRST_CONTACT_RULES.softball;
  const signingPeriods = SIGNING_PERIODS_2025_26[sport] || [];
  const divLabel = DIV_LABEL[division];

  return (
    <div className="space-y-3">
      <h3 className="font-montserrat font-semibold text-sm text-white">Key Recruiting Milestones</h3>

      {/* First contact rules */}
      <div className="rounded-xl border border-[#415A77]/40 bg-[#0D1B2A]/60 p-4">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#E0A458] mb-3">
          When Can Coaches First Contact Your Athlete? ({divLabel})
        </p>
        {division === "D2" ? (
          <p className="text-sm text-slate-300">
            DII programs generally have fewer contact restrictions than DI. Contact often begins earlier
            and dead/shutdown periods are less frequent. Verify exact rules with{" "}
            <a href="https://www.ncaa.org/sports/2018/5/8/division-i-and-ii-recruiting-calendars.aspx" target="_blank" rel="noopener noreferrer" className="text-[#E0A458] hover:underline">
              the official NCAA DII calendar
            </a>{" "}
            and your school's compliance office.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Mail className="h-4 w-4 text-sky-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-white">Email &amp; Text</p>
                <p className="text-xs text-slate-400">{contactRules.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-white">Phone Calls</p>
                <p className="text-xs text-slate-400">{contactRules.phone}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-white">In-Person Contact</p>
                <p className="text-xs text-slate-400">{contactRules.inPerson}</p>
              </div>
            </div>
          </div>
        )}
        <p className="text-xs text-slate-500 mt-3 border-t border-[#415A77]/30 pt-2">
          NCAA rules change. Always verify at ncaa.org and with your school's compliance office.
        </p>
      </div>

      {/* Signing periods */}
      {signingPeriods.length > 0 && (
        <div className="rounded-xl border border-[#415A77]/40 bg-[#0D1B2A]/60 p-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#E0A458] mb-3">
            2025-26 Signing Periods
          </p>
          <div className="space-y-3">
            {signingPeriods.map((sp, i) => {
              const signingDate = new Date(sp.dates.split(" – ")[0]);
              const isPast = !isNaN(signingDate.getTime()) && signingDate < new Date();
              return (
                <div key={i} className="flex items-start gap-3">
                  <PenLine className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isPast ? "text-slate-500" : "text-[#E0A458]"}`} />
                  <div>
                    <p className={`text-xs font-semibold ${isPast ? "text-slate-500" : "text-white"}`}>{sp.label}</p>
                    <p className={`text-xs ${isPast ? "text-slate-600" : "text-slate-300"}`}>{sp.dates}</p>
                    {sp.note && <p className="text-xs text-slate-500 mt-0.5">{sp.note}</p>}
                    {isPast && <p className="text-xs text-slate-600 italic">Period has passed</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function CoreCoursesPanel({ division }: { division: "D1" | "D2" }) {
  const courses = division === "D1" ? D1_CORE_COURSES : D2_CORE_COURSES;
  const gpa = division === "D1" ? "2.3" : "2.2";
  const isDI = division === "D1";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-[#E0A458]/30 bg-[#0D1B2A]/60 p-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#E0A458] mb-2">Core GPA</p>
          <div className="flex items-end gap-4 mt-1">
            <div>
              <p className="text-xs text-slate-400 mb-0.5">NCAA Minimum</p>
              <p className="font-montserrat font-bold text-2xl text-white">{gpa}</p>
            </div>
            <div className="border-l border-[#415A77]/60 pl-4">
              <p className="text-xs text-slate-400 mb-0.5">Coaches Want</p>
              <p className="font-montserrat font-bold text-2xl text-[#E0A458]">3.0+</p>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-2">Meeting the minimum makes you eligible. A 3.0+ makes you recruitable.</p>
        </div>
        <div className="rounded-xl border border-[#415A77]/40 bg-[#0D1B2A]/60 p-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#E0A458] mb-1">Total Core Courses</p>
          <p className="font-montserrat font-bold text-3xl text-white">16</p>
          <p className="text-xs text-slate-400 mt-1">
            {isDI
              ? "10 of 16 must be complete before your 7th semester (start of senior year)."
              : "All 16 must be complete before your first semester of full-time college enrollment."}
          </p>
        </div>
      </div>

      {isDI && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-900/20 p-3">
          <AlertCircle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-200">
            <span className="font-semibold">DI 10/7 rule:</span> 10 of your 16 core courses must be completed before your 7th semester (start of senior year), including at least 7 in English, math, or science. Once your 7th semester begins, those courses cannot be repeated or replaced.
          </p>
        </div>
      )}

      {!isDI && (
        <div className="flex items-start gap-3 rounded-xl border border-sky-500/30 bg-sky-900/20 p-3">
          <AlertCircle className="h-5 w-5 text-sky-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-sky-200">
            <span className="font-semibold">DII note:</span> No junior-year course progression requirement. All 16 core courses must be completed before your first semester of full-time college enrollment. Partial Qualifier status allows athletics aid and practice in year one, but no competition.
          </p>
        </div>
      )}

      <div className="rounded-xl border border-[#415A77]/40 bg-[#0D1B2A]/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-[#415A77]/40">
          <p className="font-semibold text-sm text-white">NCAA {DIV_LABEL[division]} Required Subjects</p>
        </div>
        <div className="divide-y divide-[#415A77]/30">
          {courses.map((c) => (
            <div key={c.subject} className="flex items-center justify-between px-4 py-3 gap-4">
              <div>
                <p className="text-sm font-medium text-white">{c.subject}</p>
                <p className="text-xs text-slate-400">{c.detail}</p>
              </div>
              <Badge variant="outline" className="bg-[#E0A458]/10 text-[#E0A458] border-[#E0A458]/30 whitespace-nowrap">
                {c.required}
              </Badge>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-[#415A77]/40 bg-[#0B1220] p-3">
        <ExternalLink className="h-4 w-4 text-[#E0A458] flex-shrink-0" />
        <p className="text-xs text-slate-300">
          Verify your specific courses at the{" "}
          <a href="https://www.ncaa.org/sports/2014/10/6/core-courses.aspx" target="_blank" rel="noopener noreferrer" className="text-[#E0A458] hover:underline">NCAA Core Courses page</a>
          {" "}and{" "}
          <a href="https://www.ncsasports.org/ncaa-eligibility-center/ncaa-core-courses" target="_blank" rel="noopener noreferrer" className="text-[#E0A458] hover:underline">NCSA's eligibility guide</a>.
        </p>
      </div>
    </div>
  );
}

function SchoolLogo({ school }: { school: CollegeInterest }) {
  if (school.espnId) {
    return (
      <img
        src={getSchoolLogoUrl(school.espnId)}
        alt={school.collegeName}
        className="h-12 w-12 object-contain flex-shrink-0"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    );
  }
  return (
    <div className="h-12 w-12 rounded-full bg-[#1B263B] border border-[#415A77]/60 flex items-center justify-center flex-shrink-0">
      <span className="font-montserrat font-bold text-sm text-[#E0A458]">
        {school.collegeName.slice(0, 2).toUpperCase()}
      </span>
    </div>
  );
}

function CollegeInterestPanel({
  interests, onAdd, onEdit, onDelete, onEmailClick,
}: {
  interests: CollegeInterest[];
  onAdd: (c: CollegeInterest) => void;
  onEdit: (id: string, updates: Partial<CollegeInterest>) => void;
  onDelete: (id: string) => void;
  onEmailClick: (collegeName: string) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [comboOpen, setComboOpen] = useState(false);
  const [comboFilter, setComboFilter] = useState<"D1" | "D2">("D1");
  const [form, setForm] = useState<{ collegeName: string; espnId?: number; interestLevel: number; notes: string }>({
    collegeName: "", espnId: undefined, interestLevel: 3, notes: "",
  });

  const resetForm = () => {
    setForm({ collegeName: "", espnId: undefined, interestLevel: 3, notes: "" });
    setShowForm(false);
    setEditingId(null);
    setComboOpen(false);
  };

  const filteredSchools = useMemo(
    () => NCAA_SCHOOLS.filter((s) => s.division === comboFilter),
    [comboFilter]
  );

  const handleSave = () => {
    if (!form.collegeName.trim()) return;
    if (editingId) {
      onEdit(editingId, form);
    } else {
      onAdd({ id: crypto.randomUUID(), ...form, addedDate: new Date().toISOString().slice(0, 10) });
    }
    resetForm();
  };

  const startEdit = (c: CollegeInterest) => {
    setForm({ collegeName: c.collegeName, espnId: c.espnId, interestLevel: c.interestLevel, notes: c.notes });
    setEditingId(c.id);
    setShowForm(true);
  };

  const selectSchool = (school: NCAASchool) => {
    setForm((f) => ({ ...f, collegeName: school.name, espnId: school.espnId }));
    setComboOpen(false);
  };

  return (
    <div className="space-y-4">
      {interests.length === 0 && !showForm && (
        <div className="flex flex-col items-center py-10 text-center">
          <School className="h-10 w-10 text-[#E0A458]/40 mb-3" />
          <p className="text-slate-300 font-semibold">No schools tracked yet</p>
          <p className="text-sm text-slate-400 mt-1">Add schools you're interested in and keep notes on each one.</p>
        </div>
      )}

      {interests.map((c) => (
        <div key={c.id} className="rounded-xl border border-[#415A77]/40 bg-[#0D1B2A]/50 p-4">
          <div className="flex items-start gap-3">
            <SchoolLogo school={c} />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-white text-base truncate">{c.collegeName}</p>
                <div className="flex gap-1 flex-shrink-0">
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-white" onClick={() => startEdit(c)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-rose-400" onClick={() => onDelete(c.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="flex mt-1 mb-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className={`h-3.5 w-3.5 ${i <= c.interestLevel ? "text-[#E0A458] fill-[#E0A458]" : "text-slate-600"}`} />
                ))}
              </div>
              {c.notes && <p className="text-sm text-slate-300 leading-relaxed">{c.notes}</p>}
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={() => onEmailClick(c.collegeName)} className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold text-xs">
              <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Generate Coach Email
            </Button>
            {c.addedDate && (
              <span className="flex items-center text-xs text-slate-500 ml-auto">
                <Clock className="h-3 w-3 mr-1" /> Added {formatShortDate(c.addedDate)}
              </span>
            )}
          </div>
        </div>
      ))}

      {showForm ? (
        <div className="rounded-xl border border-[#E0A458]/30 bg-[#0D1B2A]/80 p-4 space-y-3">
          <p className="font-semibold text-white">{editingId ? "Edit school" : "Add a school"}</p>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs text-slate-400">School name</p>
              <div className="flex gap-1">
                {(["D1", "D2"] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => { setComboFilter(d); setForm((f) => ({ ...f, collegeName: "", espnId: undefined })); }}
                    className={`text-xs px-2 py-0.5 rounded font-semibold border transition-colors ${comboFilter === d ? "bg-[#E0A458] text-black border-[#E0A458]" : "bg-transparent text-slate-400 border-[#415A77]/60 hover:border-[#E0A458]/50 hover:text-white"}`}
                  >
                    {d === "D1" ? "DI" : "DII"}
                  </button>
                ))}
              </div>
            </div>
            <Popover open={comboOpen} onOpenChange={setComboOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboOpen}
                  className="w-full justify-between bg-[#0B1220] border-[#415A77]/60 text-left font-normal hover:bg-[#0B1220] hover:border-[#E0A458]/50"
                >
                  <span className={form.collegeName ? "text-white" : "text-slate-500"}>
                    {form.collegeName || `Search ${comboFilter === "D1" ? "DI" : "DII"} schools...`}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-[#0D1B2A] border-[#415A77]/60" align="start">
                <Command className="bg-transparent">
                  <CommandInput placeholder="Type to search..." className="text-white placeholder:text-slate-500 border-b border-[#415A77]/60" />
                  <CommandList className="max-h-64">
                    <CommandEmpty className="py-4 text-center text-sm text-slate-400">No schools found.</CommandEmpty>
                    <CommandGroup>
                      {filteredSchools.map((school) => (
                        <CommandItem
                          key={school.name}
                          value={school.name}
                          onSelect={() => selectSchool(school)}
                          className="flex items-center gap-2 cursor-pointer text-slate-200 hover:bg-[#1B263B] aria-selected:bg-[#1B263B]"
                        >
                          <Check className={`h-4 w-4 flex-shrink-0 ${form.collegeName === school.name ? "opacity-100 text-[#E0A458]" : "opacity-0"}`} />
                          <span className="flex-1">{school.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1.5">Interest level</p>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <button key={i} type="button" onClick={() => setForm((f) => ({ ...f, interestLevel: i }))}>
                  <Star className={`h-6 w-6 ${i <= form.interestLevel ? "text-[#E0A458] fill-[#E0A458]" : "text-slate-600"}`} />
                </button>
              ))}
            </div>
          </div>
          <Textarea placeholder="Notes (coach contact, campus visit, scholarship info, etc.)" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="bg-[#0B1220] border-[#415A77]/60 text-white placeholder:text-slate-500 resize-none" rows={3} />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold">Save</Button>
            <Button size="sm" variant="ghost" onClick={resetForm} className="text-slate-400 hover:text-white">Cancel</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setShowForm(true)} className="border-[#415A77]/60 text-slate-300 hover:text-white hover:border-[#E0A458]/50">
          <Plus className="h-4 w-4 mr-1.5" /> Add School
        </Button>
      )}
    </div>
  );
}

// ---- Main Component ----

const Recruiting = () => {
  const { athlete } = useActiveAthlete();

  const hasAthleteProfile = Boolean(athlete?.id);
  const athleteSportRaw = athlete?.sports?.trim() || "softball";
  const defaultSportKey = normalizeSportKey(athleteSportRaw);
  const [selectedSport, setSelectedSport] = useState(defaultSportKey);
  const [selectedDivision, setSelectedDivision] = useState<"D1" | "D2">("D1");
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailDefaultCollege, setEmailDefaultCollege] = useState("");

  useEffect(() => {
    if (athlete?.sports) setSelectedSport(normalizeSportKey(athlete.sports));
  }, [athlete?.sports]);

  // Checklist state (localStorage)
  const checklistKey = `ll_recruiting_checklist_${athlete?.id || "default"}`;
  const [checklistDone, setChecklistDone] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(checklistKey) || "{}"); } catch { return {}; }
  });
  const toggleChecklist = (id: string) => {
    const next = { ...checklistDone, [id]: !checklistDone[id] };
    setChecklistDone(next);
    localStorage.setItem(checklistKey, JSON.stringify(next));
  };
  const checklistCompleted = CHECKLIST_ITEMS.filter((i) => checklistDone[i.id]).length;
  const checklistPercent = Math.round((checklistCompleted / CHECKLIST_ITEMS.length) * 100);

  // College interests — database-backed, cross-device
  const queryClient = useQueryClient();
  type DbApp = { id: string; collegeName: string; athleticInterestLevel?: string | null; notes?: string | null; createdAt?: string | null };
  const appsQueryKey = athlete?.id ? [`/api/athletes/${athlete.id}/college-applications`] : null;
  const { data: rawApplications = [] } = useQuery<DbApp[]>({
    queryKey: appsQueryKey ?? ["college-applications-disabled"],
    queryFn: () => fetchJson<DbApp[]>(`/athletes/${athlete!.id}/college-applications`),
    enabled: Boolean(athlete?.id),
  });
  const collegeInterests: CollegeInterest[] = rawApplications.map((app) => {
    const school = NCAA_SCHOOLS.find((s) => s.name === app.collegeName);
    return {
      id: app.id,
      collegeName: app.collegeName,
      espnId: school?.espnId,
      interestLevel: app.athleticInterestLevel ? (parseInt(app.athleticInterestLevel) || 3) : 3,
      notes: app.notes || "",
      addedDate: app.createdAt ? app.createdAt.slice(0, 10) : new Date().toISOString().slice(0, 10),
    };
  });
  const invalidateApps = () => { if (appsQueryKey) queryClient.invalidateQueries({ queryKey: appsQueryKey }); };
  const { toast } = useToast();
  const throwIfNotOk = async (res: Response) => {
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error((data as any).message || `Request failed (${res.status})`);
    }
    return res;
  };
  const addCollegeMutation = useMutation({
    mutationFn: (c: CollegeInterest) => apiRequest(`/athletes/${athlete!.id}/college-applications`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ collegeName: c.collegeName, status: "interested", notes: c.notes, athleticInterestLevel: String(c.interestLevel) }),
    }).then(throwIfNotOk),
    onSuccess: invalidateApps,
    onError: (err) => toast({ title: "Failed to save school", description: err instanceof Error ? err.message : "Try again.", variant: "destructive" }),
  });
  const editCollegeMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CollegeInterest> }) => apiRequest(`/college-applications/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...(updates.notes !== undefined && { notes: updates.notes }), ...(updates.interestLevel !== undefined && { athleticInterestLevel: String(updates.interestLevel) }), ...(updates.collegeName !== undefined && { collegeName: updates.collegeName }) }),
    }).then(throwIfNotOk),
    onSuccess: invalidateApps,
    onError: (err) => toast({ title: "Failed to update school", description: err instanceof Error ? err.message : "Try again.", variant: "destructive" }),
  });
  const deleteCollegeMutation = useMutation({
    mutationFn: (id: string) => apiRequest(`/college-applications/${id}`, { method: "DELETE" }).then(throwIfNotOk),
    onSuccess: invalidateApps,
    onError: (err) => toast({ title: "Failed to delete school", description: err instanceof Error ? err.message : "Try again.", variant: "destructive" }),
  });
  const addCollege = (c: CollegeInterest) => addCollegeMutation.mutate(c);
  const editCollege = (id: string, updates: Partial<CollegeInterest>) => editCollegeMutation.mutate({ id, updates });
  const deleteCollege = (id: string) => deleteCollegeMutation.mutate(id);

  const openEmailFor = (collegeName: string) => { setEmailDefaultCollege(collegeName); setEmailModalOpen(true); };

  // Client-side calendar computation — no API dependency
  const today = useMemo(() => new Date(), []);
  const calCurrentPeriod = useMemo(
    () => getCalendarCurrentPeriod(selectedSport, selectedDivision, today),
    [selectedSport, selectedDivision, today]
  );
  const calPeriodStatus = useMemo(
    () => getCalendarPeriodStatus(selectedSport, selectedDivision, today),
    [selectedSport, selectedDivision, today]
  );
  const calKeyDates = useMemo(
    () => getCalendarKeyDates(selectedSport, selectedDivision, today).map((e) => ({
      ...e,
      division: DIV_LABEL[selectedDivision],
    })),
    [selectedSport, selectedDivision, today]
  );

  const upcomingMilestones = useMemo(() => getUpcomingMilestones(today), [today]);
  const gradYearMessage = getGradYearMessage(athlete?.graduationYear);
  const missingFields: string[] = [];
  if (!athlete?.sports?.trim()) missingFields.push("sport");
  if (typeof athlete?.graduationYear !== "number") missingFields.push("graduation year");
  const isUsingDefaults = !hasAthleteProfile || missingFields.length > 0;
  const sportLabel = SPORT_OPTIONS.find((s) => s.value === selectedSport)?.label || selectedSport;

  return (
    <div className="container mx-auto min-h-screen px-4 py-6 text-white" style={legacyTheme}>
      {/* Page banner */}
      <div className="mb-6 flex flex-col gap-4 rounded-xl border border-[#E0A458]/45 bg-gradient-to-r from-[#0D1B2A] to-[#1B263B] px-4 py-4 shadow-lg shadow-black/25 md:flex-row md:items-center md:justify-between">
        <h1 className="font-montserrat font-bold text-2xl md:text-3xl text-white">Recruiting Center</h1>
        <Button onClick={() => { setEmailDefaultCollege(""); setEmailModalOpen(true); }} className="bg-[#E0A458] text-black hover:bg-[#f0b467] flex items-center font-semibold">
          <Wand2 className="h-4 w-4 mr-2" /> Generate Coach Email
        </Button>
      </div>

      {/* Profile incomplete notice */}
      {isUsingDefaults && (
        <Card className="mb-6 border-[#E0A458]/45 bg-[#172844] shadow-lg shadow-black/25">
          <CardHeader className="border-b border-[#E0A458]/30 bg-gradient-to-r from-[#2B4368]/70 via-[#223657]/65 to-[#172844]/70 pb-3">
            <CardTitle className="text-base">
              {!hasAthleteProfile ? "Using default recruiting setup" : `Finish profile setup: ${missingFields.join(" and ")}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 text-sm text-slate-300">
            <p>{!hasAthleteProfile ? "Showing DI Softball defaults. Add your athlete sport and graduation year to personalize this page." : "Complete your profile for fully personalized recruiting guidance."}</p>
            <div className="mt-4">
              <Link href="/welcome-setup?next=%2Frecruiting">
                <Button className="bg-[#E0A458] text-black hover:bg-[#f0b467]">Complete Profile Setup</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grad year context */}
      <Card className="mb-6 border-[#E0A458]/45 bg-[#172844] shadow-lg shadow-black/25">
        <CardHeader className="border-b border-[#E0A458]/30 bg-gradient-to-r from-[#2B4368]/70 via-[#223657]/65 to-[#172844]/70 pb-3">
          <CardTitle className="text-base">{gradYearMessage.title}</CardTitle>
        </CardHeader>
        <CardContent className="pt-4 text-sm text-slate-300">
          <p>{gradYearMessage.body}</p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main tabs */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="timeline">
            <TabsList className="mb-4 grid h-auto w-full grid-cols-2 gap-2 rounded-xl border border-[#E0A458]/45 bg-[#111E34] p-2 sm:grid-cols-4">
              {(["timeline", "core", "colleges", "checklist"] as const).map((tab) => (
                <TabsTrigger key={tab} value={tab} className="rounded-lg border border-[#415A77] bg-[#0B1220] px-4 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:border-[#E0A458]/70 hover:text-white data-[state=active]:border-[#E0A458] data-[state=active]:bg-[#E0A458] data-[state=active]:text-[#0D1B2A]">
                  {tab === "timeline" ? "NCAA Timeline" : tab === "core" ? "Core Courses" : tab === "colleges" ? "College Interests" : "Checklist"}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* NCAA Timeline */}
            <TabsContent value="timeline">
              <Card className="border-[#415A77]/40 bg-[#111827]">
                <CardHeader className="border-b border-[#E0A458]/20 pb-3">
                  <CardTitle className="text-lg font-montserrat font-bold">NCAA Recruiting Timeline</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5 pt-5">
                  {/* Sport + Division selector */}
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[160px]">
                      <p className="text-xs text-slate-400 mb-1.5">Sport</p>
                      <Select value={selectedSport} onValueChange={setSelectedSport}>
                        <SelectTrigger className="bg-[#0B1220] border-[#415A77]/60 text-white h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0D1B2A] border-[#415A77]/60 text-white">
                          {SPORT_OPTIONS.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 mb-1.5">Division</p>
                      <div className="flex gap-1.5">
                        {(["D1", "D2"] as const).map((d) => (
                          <Button key={d} size="sm" variant="outline" onClick={() => setSelectedDivision(d)}
                            className={`h-9 px-4 font-semibold border transition-colors ${selectedDivision === d ? "bg-[#E0A458] text-black border-[#E0A458] hover:bg-[#f0b467]" : "bg-[#0B1220] text-slate-300 border-[#415A77]/60 hover:border-[#E0A458]/50 hover:text-white"}`}>
                            {DIV_LABEL[d]}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Contact status widget */}
                  <ContactStatusWidget
                    currentPeriod={calCurrentPeriod}
                    division={DIV_LABEL[selectedDivision]}
                    sport={sportLabel}
                  />

                  {/* Key milestones: first contact rules + signing periods */}
                  <KeyMilestonesSection sport={selectedSport} division={selectedDivision} />

                  {/* Period status cards + key dates timeline */}
                  <RecruitingTimeline
                    events={calKeyDates}
                    athleteSport={selectedSport}
                    division={DIV_LABEL[selectedDivision]}
                    detailed
                    periodStatus={calPeriodStatus}
                  />

                  {/* Disclaimer */}
                  <div className="rounded-md border border-[#415A77]/40 bg-[#0B1220]/60 p-3 text-xs text-slate-400 leading-relaxed">
                    Dates are subject to change. Always confirm your sport's exact recruiting calendar using the links in <span className="text-slate-300">Official Resources</span> before making any recruiting decisions.
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Core Courses */}
            <TabsContent value="core">
              <Card className="border-[#415A77]/40 bg-[#111827]">
                <CardHeader className="border-b border-[#E0A458]/20 pb-3">
                  <CardTitle className="text-lg font-montserrat font-bold flex items-center gap-2">
                    <GraduationCap className="h-5 w-5 text-[#E0A458]" /> Core Courses &amp; Eligibility
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5 pt-5">
                  <div className="flex gap-2">
                    {(["D1", "D2"] as const).map((d) => (
                      <Button key={d} size="sm" variant="outline" onClick={() => setSelectedDivision(d)}
                        className={`font-semibold border transition-colors ${selectedDivision === d ? "bg-[#E0A458] text-black border-[#E0A458] hover:bg-[#f0b467]" : "bg-[#0B1220] text-slate-300 border-[#415A77]/60 hover:border-[#E0A458]/50 hover:text-white"}`}>
                        {DIV_LABEL[d]}
                      </Button>
                    ))}
                  </div>
                  <CoreCoursesPanel division={selectedDivision} />
                  {athlete?.id ? (
                    <div className="pt-2">
                      <Separator className="border-[#415A77]/30 mb-5" />
                      <p className="font-semibold text-sm text-slate-300 mb-3">Track Your Athlete's Credits</p>
                      <AcademicRequirementsTracker athleteId={athlete.id} />
                    </div>
                  ) : (
                    <div className="rounded-md border border-[#415A77]/60 bg-[#0B1220] p-4 text-sm text-slate-300">
                      Create your athlete profile to track individual core-course progress.
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* College Interests */}
            <TabsContent value="colleges">
              <Card className="border-[#415A77]/40 bg-[#111827]">
                <CardHeader className="border-b border-[#E0A458]/20 pb-3">
                  <CardTitle className="text-lg font-montserrat font-bold">College Interest Tracker</CardTitle>
                </CardHeader>
                <CardContent className="pt-5">
                  <CollegeInterestPanel interests={collegeInterests} onAdd={addCollege} onEdit={editCollege} onDelete={deleteCollege} onEmailClick={openEmailFor} />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Recruiting Checklist */}
            <TabsContent value="checklist">
              <Card className="border-[#415A77]/40 bg-[#111827]">
                <CardHeader className="border-b border-[#E0A458]/20 pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-montserrat font-bold">Recruiting Checklist</CardTitle>
                    <span className="text-sm text-slate-400">{checklistCompleted}/{CHECKLIST_ITEMS.length} complete</span>
                  </div>
                  <div className="h-1.5 w-full bg-[#415A77]/30 rounded-full mt-3 overflow-hidden">
                    <div className="h-full bg-[#E0A458] rounded-full transition-all duration-500" style={{ width: `${checklistPercent}%` }} />
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-1">
                    {CHECKLIST_ITEMS.map((item, idx) => {
                      const done = !!checklistDone[item.id];
                      return (
                        <div key={item.id}>
                          <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-[#18233a] transition-colors">
                            <button type="button" onClick={() => toggleChecklist(item.id)} className="flex-shrink-0 mt-0.5">
                              {done ? <CheckCircle2 className="h-5 w-5 text-[#E0A458]" /> : <Circle className="h-5 w-5 text-slate-600" />}
                            </button>
                            <div className="flex-grow min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <p className={`font-medium text-sm ${done ? "line-through text-slate-500" : "text-white"}`}>{item.title}</p>
                                {item.deadline && !done && <span className="text-xs text-slate-400 flex-shrink-0">{item.deadline}</span>}
                              </div>
                              {!done && <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{item.description}</p>}
                            </div>
                          </div>
                          {idx < CHECKLIST_ITEMS.length - 1 && <Separator className="border-[#415A77]/20" />}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Upcoming Milestones — student-athlete action items */}
          <Card className="border-[#E0A458]/30 bg-[#172844]">
            <CardHeader className="border-b border-[#E0A458]/30 bg-gradient-to-r from-[#2B4368]/70 via-[#223657]/65 to-[#172844]/70 pb-3">
              <CardTitle className="text-lg flex items-center font-montserrat font-bold">
                <Calendar className="h-5 w-5 mr-2 text-[#E0A458]" /> Upcoming Deadlines
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {upcomingMilestones.length > 0 ? (
                <ul className="space-y-3">
                  {upcomingMilestones.map((m, i) => (
                    <li key={i} className="flex justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-sm leading-snug">{m.title}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{formatShortDate(m.date)}</p>
                        <p className="text-xs text-slate-500 mt-0.5 leading-snug">{m.description}</p>
                      </div>
                      <div className="flex-shrink-0">
                        <Badge variant="outline" className={`${MILESTONE_COLORS[m.category]} text-xs whitespace-nowrap`}>
                          {m.daysUntil === 0 ? "Today" : `${m.daysUntil}d`}
                        </Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-400">No upcoming milestones on record.</p>
              )}
              <p className="text-xs text-slate-600 mt-4 border-t border-[#415A77]/20 pt-3">Dates are approximate. Verify all test and application deadlines at the official source.</p>
            </CardContent>
          </Card>

          {/* Recruiting Progress */}
          <Card className="border-[#E0A458]/30 bg-[#172844]">
            <CardHeader className="border-b border-[#E0A458]/30 bg-gradient-to-r from-[#2B4368]/70 via-[#223657]/65 to-[#172844]/70 pb-3">
              <CardTitle className="text-lg font-montserrat font-bold">Recruiting Progress</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div>
                <div className="flex justify-between mb-1.5">
                  <span className="text-sm font-medium">Checklist</span>
                  <span className="text-sm font-medium text-[#E0A458]">{checklistPercent}%</span>
                </div>
                <Progress value={checklistPercent} className="h-2" />
                <p className="text-xs text-slate-400 mt-1">{checklistCompleted} of {CHECKLIST_ITEMS.length} steps complete</p>
              </div>
              <div>
                <div className="flex justify-between mb-1.5">
                  <span className="text-sm font-medium">Schools Tracked</span>
                  <span className="text-sm font-medium text-[#E0A458]">{collegeInterests.length}</span>
                </div>
                <Progress value={Math.min(collegeInterests.length * 10, 100)} className="h-2" />
                <p className="text-xs text-slate-400 mt-1">Goal: 10 schools</p>
              </div>
            </CardContent>
          </Card>

          {/* Official Resources */}
          <Card className="border-[#E0A458]/30 bg-[#172844]">
            <CardHeader className="border-b border-[#E0A458]/30 bg-gradient-to-r from-[#2B4368]/70 via-[#223657]/65 to-[#172844]/70 pb-3">
              <CardTitle className="text-lg font-montserrat font-bold">Official Resources</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <ul className="space-y-2 text-sm">
                {OFFICIAL_RESOURCES.map((resource) => (
                  <li key={resource.url} className="flex items-start gap-2">
                    <ExternalLink className="h-4 w-4 mt-0.5 text-[#E0A458] flex-shrink-0" />
                    <a href={resource.url} target="_blank" rel="noopener noreferrer" className="text-[#E0A458] hover:underline leading-snug">{resource.title}</a>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      <CoachEmailWriter open={emailModalOpen} onClose={() => setEmailModalOpen(false)} defaultCollege={emailDefaultCollege} athleteId={athlete?.id} athleteName={athlete?.name} />
    </div>
  );
};

export default Recruiting;
